import { MdArticle, MdDelete, MdEdit, MdPlayArrow } from 'react-icons/md';
import { IconButton } from '@mui/material';
import { formatDate, formatRelativeTime } from '@utils/format';
import { hideOnImgError, imageUrl } from '@utils/imageUrl';
import {
  NEWS_ARTICLE_STATUS_LABELS,
  type NewsArticle,
  type NewsArticleStatus,
} from '../schema';

interface Props {
  row: NewsArticle;
  onEdit: (row: NewsArticle) => void;
  onDelete: (row: NewsArticle) => void;
}

// Tones diverge intentionally from StatusBadge's desktop palette:
// the mobile card uses warning for draft + muted for archived to read at
// a glance, while the desktop badge uses secondary/warning. Labels are
// shared via NEWS_ARTICLE_STATUS_LABELS; tones stay local.
const STATUS_TONES: Record<NewsArticleStatus, string> = {
  draft: 'bg-warning/[0.12] text-warning',
  published: 'bg-success/[0.14] text-success',
  archived: 'bg-foreground-muted/[0.14] text-foreground-muted',
};

/**
 * Mobile card view for a news article row. Rendered by `<ResponsiveDataList>`
 * below md (768px).
 *
 * Tappable surface is the whole card (opens edit dialog). Edit + Delete are
 * also surfaced as explicit action buttons on the right so the user doesn't
 * have to open the edit dialog just to delete. Video articles get a
 * play-icon badge over the thumbnail.
 */
export function NewsArticleCard({ row, onEdit, onDelete }: Props) {
  const thumb = imageUrl(row.thumbnailStorageKey ?? row.imageStorageKey);
  const isVideo = row.kind === 'video';
  const dateText = row.publishedAt
    ? `Published ${formatDate(row.publishedAt)}`
    : `Updated ${formatRelativeTime(row.updatedAt)}`;

  return (
    <div className="group relative flex gap-3 rounded-xl border border-border bg-surface p-3 transition-colors hover:border-primary/40 hover:bg-primary/[0.02]">
      <button
        type="button"
        onClick={() => onEdit(row)}
        className="absolute inset-0 z-0 rounded-xl"
        aria-label={`Edit ${row.title}`}
      />

      <div className="relative z-10 shrink-0">
        {thumb ? (
          <div className="relative h-16 w-16 overflow-hidden rounded-lg ring-1 ring-border">
            <img
              src={thumb}
              alt=""
              className="h-full w-full object-cover"
              onError={hideOnImgError}
            />
            {isVideo && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                <MdPlayArrow className="text-white" size={28} />
              </div>
            )}
          </div>
        ) : (
          <div className="flex h-16 w-16 items-center justify-center rounded-lg bg-surface-muted text-foreground-muted ring-1 ring-border">
            <MdArticle size={24} />
          </div>
        )}
      </div>

      <div className="relative z-10 min-w-0 flex-1 pointer-events-none">
        <div className="flex items-start gap-2">
          <h3 className="line-clamp-2 min-w-0 flex-1 text-[14.5px] font-semibold leading-tight text-foreground">
            {row.title}
          </h3>
          <span
            className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${STATUS_TONES[row.status]}`}
          >
            {NEWS_ARTICLE_STATUS_LABELS[row.status]}
          </span>
        </div>
        {row.summary && (
          <p className="mt-1 line-clamp-2 text-[12.5px] leading-snug text-foreground-muted">
            {row.summary}
          </p>
        )}
        <div className="mt-1.5 truncate text-[11px] text-foreground-muted">
          {dateText} · <code className="font-mono">{row.slug}</code>
        </div>
      </div>

      <div className="relative z-10 flex shrink-0 flex-col gap-1">
        <IconButton
          size="small"
          onClick={() => onEdit(row)}
          aria-label={`Edit ${row.title}`}
        >
          <MdEdit size={18} />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => onDelete(row)}
          aria-label={`Delete ${row.title}`}
          className="text-danger"
        >
          <MdDelete size={18} />
        </IconButton>
      </div>
    </div>
  );
}
